import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Dscr = () => {
  // State variables for DSCR calculation
  const [netOperatingIncome, setNetOperatingIncome] = useState(0); // Net Operating Income
  const [totalDebtService, setTotalDebtService] = useState(0); // Total Debt Service
  const [dscr, setDSCR] = useState(0); // Calculated DSCR

  // Event handlers for input changes
  const handleNetOperatingIncomeChange = (e) =>
    setNetOperatingIncome(parseFloat(e.target.value) || 0);
  const handleTotalDebtServiceChange = (e) =>
    setTotalDebtService(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate DSCR
  useEffect(() => {
    if (totalDebtService > 0) {
      const ratio = netOperatingIncome / totalDebtService;
      setDSCR(ratio);
    } else {
      setDSCR(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [netOperatingIncome, totalDebtService]); // Recalculates when inputs change

  return (
    <Layout title="DSCR Calculator" path="dscr-calculator">
      <section className="section-padding" id="dscr-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* DSCR Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Debt Service Coverage Ratio (DSCR) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Net Operating Income */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="netOperatingIncome" className="form-label">
                        Net Operating Income
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Net Operating Income (NOI):</strong> The total income generated from property operations after expenses. It is calculated as total revenue minus operating expenses."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="netOperatingIncome"
                        placeholder="Enter Net Operating Income"
                        value={netOperatingIncome || ""}
                        onChange={handleNetOperatingIncomeChange}
                      />
                    </div>

                    {/* Input for Total Debt Service */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalDebtService" className="form-label">
                        Total Debt Service
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Debt Service:</strong> The total amount of debt payments (principal and interest) that a borrower must pay over a given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalDebtService"
                        placeholder="Enter Total Debt Service"
                        value={totalDebtService || ""}
                        onChange={handleTotalDebtServiceChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Debt Service Coverage Ratio (DSCR):{" "}
                        <span className="fw-bold ms-3">
                          {dscr === 0
                            ? "0.00"
                            : dscr.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to DSCR Calculation
                </h3>

                {/* What is DSCR? */}
                <h4 className="mt-3 mb-3">What is Debt Service Coverage Ratio (DSCR)?</h4>
                <p>
                  **Debt Service Coverage Ratio (DSCR)** is a financial metric that measures an entity's ability to cover its debt obligations using its net operating income. It indicates financial stability and repayment capacity.
                </p>

                {/* Why Use the DSCR Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the DSCR Calculator?</h4>
                <p>
                  This calculator provides a quick and reliable way to assess whether an entity has sufficient income to meet its debt obligations, helping financial professionals make informed decisions.
                </p>

                {/* Benefits of Using the DSCR Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the DSCR Calculator</h4>
                <ul>
                  <li>
                    <strong>Evaluates Repayment Capacity:</strong> Helps determine whether an entity can meet its financial commitments.
                  </li>
                  <li>
                    <strong>Informs Lending Decisions:</strong> Essential for banks and financial institutions assessing loan applications.
                  </li>
                  <li>
                    <strong>Supports Financial Planning:</strong> Assists businesses in evaluating their financial health and risk.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the DSCR Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Banks:</strong> To assess loan repayment risks and approve credit.
                  </li>
                  <li>
                    <strong>Lending Institutions:</strong> To evaluate the financial stability of borrowers.
                  </li>
                  <li>
                    <strong>Financial Advisors:</strong> To provide strategic insights for clients managing debt.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">What is a good DSCR value?</h5>
                <p>
                  A DSCR of 1 or higher is typically considered good. Values below 1 indicate that an entity may struggle to meet debt obligations.
                </p>

                <h5 className="mt-3 mb-2">How can businesses improve their DSCR?</h5>
                <p>
                  Businesses can improve their DSCR by increasing net operating income, reducing debt obligations, or refinancing existing debt at lower interest rates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Dscr;
